/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import { getMetricUnitDesc } from '@features/utr-modal/components/chart';
import UniversalTracker from '../../../../model/UniversalTracker';
import { getChartColors, getChartDataByValues, getChartMaxValue, isAssuranceRestated } from '../../../../utils/charts';
import { getChartOptions, toChartProps } from '@routes/summary/insights/utils/chartStyles';
import { ChartType } from '../../../summary/insights/utils/constants';
import { SingleValueWrapper } from './SingleValueWrapper';
import Chart from 'react-google-charts';
import {
  ChartSubType,
  GridDashboardItem,
  HistoricalUtrs,
  InsightDashboardItemType,
  SubType,
} from '@g17eco/types/insight-custom-dashboard';
import { isSDGTrackerType } from '../../utils';
import { DATE } from '../../../../utils/date';
import { CurrencyConfig, getActualUtrvs, getLatestActualUtrvCurrency } from './multi-utrs-chart/utils';
import { UniversalTrackerModalServiceUtrv } from '@g17eco/types/universalTrackerValue';
import { SurveyActionData } from '@g17eco/types/surveyAction';
import { getNANRMessage } from './common/getNANRMessage';
import { NANRMessage } from './common/NANRMessage';
import { FULL_DATA_NA_NR_SUBTYPES } from './common/naNrSubtypes';

interface ChartWidgetProps {
  utrData?: HistoricalUtrs;
  selectedColumnCode: string | undefined;
  subType?: SubType;
  handleSetTarget?: () => void;
  readOnly?: boolean;
  unitText?: string;
  initiativeId: string;
  survey?: Pick<SurveyActionData, '_id' | 'fragmentUniversalTrackerValues'>;
  type: InsightDashboardItemType.Chart | InsightDashboardItemType.SDGTracker;
  altUnitText?: string;
  hideQuestionReference?: boolean;
  actionButton?: JSX.Element | null;
  currencyConfig: CurrencyConfig;
}

export interface UniversalTrackerInsightData {
  utr: UniversalTracker | undefined;
  utrvs: UniversalTrackerModalServiceUtrv[];
}

const MIDDLE_OF_MONTH = 15;

/** @deprecated in favour of MultiUtrsChartWidget */
export const ChartWidget = (props: ChartWidgetProps) => {
  const {
    utrData,
    selectedColumnCode,
    subType = ChartSubType.Line,
    handleSetTarget,
    readOnly = false,
    unitText,
    initiativeId,
    survey,
    type,
    altUnitText,
    hideQuestionReference = false,
    actionButton,
    currencyConfig,
  } = props;

  const chartData = getChartDataByValues(utrData?.utrvs, selectedColumnCode);

  // @TODO: Need to consolidate styling of value: Refactor dashboard item configuration,
  // {
  //   color
  //   direction
  //   unitText
  // }

  // Generate unit text from UTR, using currencyConfig.currencyCode to override currency if provided
  // This fixes cases where UTR has "USD" but survey uses "GBP"
  const metricUnitText =
    utrData && utrData.utr
      ? getMetricUnitDesc(utrData.utr, selectedColumnCode, '', { currencyCode: currencyConfig.currencyCode })
      : '';

  const isRestated = isAssuranceRestated(utrData?.utrvs);

  if (subType === ChartSubType.SingleValue || subType === ChartSubType.SparkLine) {
    // For SingleValue, fall back to per-UTRV currency lookup when there's no consistent currency.
    // For SparkLine, we only use currencyConfig.currencyCode since the chart shows multiple entries
    // that may have different currencies - using latest UTRV's currency would be misleading.
    // This can be revisited if we need per-point currency display in SparkLine.
    const getEffectiveCurrencyCode = (): string | undefined => {
      if (currencyConfig.currencyCode) {
        return currencyConfig.currencyCode;
      }
      if (subType === ChartSubType.SingleValue) {
        return getLatestActualUtrvCurrency(utrData?.utrvs, currencyConfig.surveyUnitConfigMap)?.currencyCode;
      }
      return undefined;
    };

    const singleValueMetricUnitText =
      utrData && utrData.utr
        ? getMetricUnitDesc(utrData.utr, selectedColumnCode, '', { currencyCode: getEffectiveCurrencyCode() })
        : '';

    const isSDGTracker = isSDGTrackerType({ type } as GridDashboardItem);
    return (
      <SingleValueWrapper
        subType={subType}
        utrData={utrData}
        chartData={chartData}
        metricUnitText={singleValueMetricUnitText}
        selectedColumnCode={selectedColumnCode}
        isRestated={isRestated}
        handleSetTarget={handleSetTarget}
        readOnly={readOnly}
        unitText={unitText}
        initiativeId={initiativeId}
        survey={survey}
        type={type}
        altUnitText={altUnitText}
        haveTextsSameColor={isSDGTracker}
        hideQuestionReference={isSDGTracker || hideQuestionReference}
        actionButton={actionButton}
        currencyConfig={currencyConfig}
      />
    );
  }

  const shouldHandleTimelineNaNr = FULL_DATA_NA_NR_SUBTYPES.includes(subType as ChartSubType);
  if (shouldHandleTimelineNaNr) {
    const actualUtrvs = getActualUtrvs(utrData ? [utrData] : []);
    const naNrMessage = getNANRMessage(actualUtrvs);
    if (naNrMessage) {
      return (
        <div className='w-100 h-100 d-flex flex-column justify-content-center'>
          <NANRMessage message={naNrMessage} />
        </div>
      );
    }
  }

  const showNoData = !((chartData || []).slice(1).length > 0);
  if (showNoData) {
    return <p className='text-sm text-medium'>No available data</p>;
  }

  // Ignore the text row, first item of second row and above will be the date
  const ticks = chartData?.slice(1).map((data) => {
    const newDate = new Date(data[0] as Date);
    // Middle of month to avoid drawing 2 ticks that are too close to each other
    newDate.setDate(MIDDLE_OF_MONTH);
    return newDate;
  });

  const vAxisMaxValue = getChartMaxValue(chartData);

  // Somehow using direct <Chart /> doesn't update chart's height when this component's updated.
  const RenderedChart = () => {
    return (
      <Chart
        chartType={ChartType.LineChart}
        data={chartData}
        width={'100%'}
        height={'100%'}
        options={{
          ...getChartOptions({
            chartType: ChartType.LineChart,
            vAxisTitle: metricUnitText,
            vAxisMaxValue,
            chartProps: toChartProps(props),
          }),
          interpolateNulls: true,
          pointSize: 5,
          colors: getChartColors(isRestated),
          hAxis: {
            format: DATE.MONTH_YEAR_SHORT,
            ticks: ticks,
            slantedText: true,
            textStyle: {
              fontSize: 10,
            },
          },
        }}
      />
    );
  };

  return (
    <div className='w-100 h-100'>
      <RenderedChart />
    </div>
  );
};
